import React from "react";
import { motion } from "framer-motion";
import { Bot, User, Check, Clock } from "lucide-react";
import TypingAnimation from "./TypingAnimation";
import AudioPlayer from "./AudioPlayer";
import INPUT_TYPES from "../constants/input-types";
import SpeechSynthesis from "./SpeechSynthesis";
import MESSAGE_SENDER_TYPES from "../constants/message-sender-types";
import { markdownRemover } from "../utils/markdownRemover";
import ImageTableIcons from "./ImgAndTableButtons";
import ThumsUpDown from "./ThumsUpDown";

const ChatBubble = ({
  data,
  selectedMessageId,
  onSelect,
  imageId,
  tableId,
  dialogId,
}) => {
  const isUser = data.sender === MESSAGE_SENDER_TYPES.USER;

  function cleanMarkdown(text) {
    const cleanedMarkdown = text.replace(/[*_~`#>!-]/g, ""); //text.replace(/\\n/g, "\n");
    return cleanedMarkdown;
  }

  const time =
    data.timestamp ||
    new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <>
      <div
        className={`group flex items-end gap-3 mt-4 ${
          isUser ? "flex-row-reverse" : "flex-row"
        }`}
        data-id={data.id}
      >
        <div
          className={`
        h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0
        ${
          isUser
            ? "bg-blue-100 dark:bg-blue-900"
            : "bg-gray-100 dark:bg-gray-700"
        } 
        transition-colors
      `}
        >
          {isUser ? (
            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <Bot className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
        </div>

        <div className="max-w-[80%] flex flex-col msgBubble">
          <span
            className={`text-xs mb-1 ${
              isUser
                ? "text-right text-blue-500 dark:text-blue-400"
                : "text-left text-gray-500 dark:text-gray-400"
            } transition-colors`}
          >
            {isUser ? "You" : ""}
          </span>

          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className={`
            relative p-4 rounded-2xl shadow-sm
            ${
              isUser
                ? "bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white rounded-br-none"
                : "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-none"
            }
            transition-colors

            ${
              selectedMessageId === data.id
                ? "border-2 border-blue-500"
                : "border-2 border-transparent"
            }

          `}
          >
            <p className="text-sm font-sm whitespace-pre-wrap break-words">
              {!isUser ? (
                <TypingAnimation text={cleanMarkdown(data.message)} />
              ) : data.message_type?.toLowerCase().trim() ===
                INPUT_TYPES.TEXT ? (
                data.message
              ) : (
                <AudioPlayer file={data.message} />
              )}
            </p>

            <div
              className={`
            flex items-center gap-1 mt-1
            text-xs opacity-70
            ${isUser ? "text-blue-100" : "text-gray-500 dark:text-gray-400"}
            transition-colors
          `}
            >
              <span>{time}</span>
              {isUser && (
                <div className="flex items-center gap-0.5">
                  {data.status === "sending" ? (
                    <Clock className="w-3 h-3" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      <span
        className={`text-xs ml-10 "text-left text-gray-500 dark:text-gray-400"
         transition-colors`}
      >
        {!isUser && (
          <div className="flex items-start gap-2">
            <SpeechSynthesis
              displayText={markdownRemover(data.message)}
              onSelect={onSelect}
              messageId={data.id}
            />
            <ImageTableIcons imageId={imageId} tableId={tableId} />
            <ThumsUpDown dialogId={dialogId} />
          </div>
        )}
      </span>
    </>
  );
};

export default ChatBubble;
